import React from 'react';
import { DeviceContext } from '../../../contexts/device/device';
import { theme } from '../../../style/global-style';
import { getAssetsPrefix } from '../../../utils/buildVersion';
import { eventTracker } from '../../../utils/EventTracker';
import { __ } from '../../../utils/translation-service';
import { DreamlinesButton } from '../../atoms/dreamlines-button/dreamlines-button';
import { NextImage } from '../../atoms/image/image';
import { SectionHeadline } from '../../atoms/section-headline/section-headline';
import {
    DirectionArrow,
    PinLogo,
    StyledBgWrapper,
    StyledButtonWrapper,
    StyledContent,
    StyledLatLong,
    StyledShipLocation,
    StyledShipPositionInfo,
    StyledTitleWrapper,
} from './ship-position.style';

export interface IShipLocationPort {
    course?: number;
    latitude?: number;
    longitude?: number;
    name: string;
    speed?: string;
    nextPort?: {
        countryInitials: string;
        name: string;
    };
    lastPort?: {
        name: string;
    };
    href: string;
}

export const ShipPosition = ({
    latitude,
    longitude,
    name,
    nextPort,
    lastPort,
    href,
}: IShipLocationPort) => {
    const lastPortName = lastPort?.name;
    const nextPortName = nextPort?.name;
    const nextPortCountryInitials = nextPort?.countryInitials;
    const device = React.useContext(DeviceContext);

    if (!name && !lastPortName && !latitude && !longitude && !nextPortName) {
        return null;
    }

    const assetPrefix = getAssetsPrefix();

    return (
        <StyledBgWrapper>
            <NextImage
                placeholder={`${assetPrefix}/static/images/ship-position-bg-low-quality.jpg`}
                imgUrl={`${assetPrefix}/static/images/ship-position-bg-${device}.jpg`}
                isBackground
                fill
                customContentStyle={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <StyledTitleWrapper>
                    <SectionHeadline
                        firstChunk={name}
                        secondChunk={__('actualPosition')}
                        color="light"
                    />
                </StyledTitleWrapper>
                <StyledContent>
                    <StyledShipPositionInfo>
                        {!!lastPortName && (
                            <StyledShipLocation>
                                <PinLogo
                                    width={14}
                                    height={20}
                                    fill={theme.shades.white}
                                    margin="0 5px 0 0"
                                    transform="translateY(2px)"
                                />
                                {lastPortName}
                                {nextPortName && nextPortCountryInitials && (
                                    <>
                                        <DirectionArrow
                                            width={16}
                                            height={10}
                                            fill={theme.shades.white}
                                            margin="0 6px"
                                        />
                                        {nextPortName}, {nextPortCountryInitials}
                                    </>
                                )}
                            </StyledShipLocation>
                        )}
                        {latitude && longitude && (
                            <StyledLatLong>
                                <div>
                                    {__('latitudeLabel')}: {latitude}°
                                </div>
                                <div>
                                    {__('longitudeLabel')}: {longitude}°
                                </div>
                            </StyledLatLong>
                        )}
                    </StyledShipPositionInfo>
                    {!!href && (
                        <StyledButtonWrapper>
                            <DreamlinesButton
                                style={{
                                    height: '40px',
                                    width: '226px',
                                    color: theme.shades.white,
                                    borderColor: theme.shades.white,
                                    fontSize: theme.fontSize.copyTextL,
                                }}
                                tertiary
                                redirectUrl={href}
                                onClick={() => {
                                    eventTracker.track({
                                        event: 'click',
                                        category: 'link-to-subpage',
                                        action: 'ship-position-module',
                                        label: `ship-${name}`,
                                    });
                                }}
                            >
                                {__('shipPositionBtnLabel')}
                            </DreamlinesButton>
                        </StyledButtonWrapper>
                    )}
                </StyledContent>
            </NextImage>
        </StyledBgWrapper>
    );
};
